import { RefObject, useEffect, useRef, useState } from 'react';
import {
  useParams,
  NavLink,
  generatePath,
  useNavigate,
  useOutletContext,
  useLocation,
} from 'react-router-dom';
import isEqual from 'lodash.isequal';
import { SendMessage } from 'react-use-websocket';
import { MDXEditorMethods } from '@mdxeditor/editor';
import {
  IconLoader,
  IconRight,
  IconUpload,
  IconAttach,
  ClickOutsideDetector,
  Popup,
  PermissionEnum,
  IconUser,
  IconSelected,
  usePrevious,
  TransactionTypeEnum,
  IconSearch,
  InputText,
  InputSize,
  IconClose,
  ResponseError,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import AssignSidePanel from '../components/assign-panel';
import AttachmentSidepanel from '../components/attachments-sidepanel';
import AddFiles from '../components/add-files';
import {
  ChannelStatus,
  ChannelType,
  CustomerType,
  UploadResponse,
  Document,
  PspPartnerPaymentType,
  Channel,
} from './types';
import {
  useLazyGetMessagesQuery,
  useLazyGetMessagingEmployeesListQuery,
  useLazyGetSelectedChannelQuery,
  useAssignEmployeeToChatMutation,
  useMessagingUploadMutation,
  useSendFileMutation,
  useLazyGetUserAccountsQuery,
  useLazyGetMessagingTopUpQuery,
  useLazyGetTopUpCurrenciesQuery,
  useLazyGetMessagingTopUpInstructionsQuery,
  useLazyGetMessagingTopUpInstructionPdfQuery,
  useChangeChatStatusMutation,
  useLazyGetChannelDocumentsQuery,
  useUnreadMessagesMutation,
} from './store/services';
import {
  setMainChannels,
  setRequestChannels,
  setScrolled,
  setSelectedChannel,
  setSelectedChannelTab,
} from './store/slices/general';
import formatTitle from './helpers/format-title';
import sendMessageHelper from './helpers/send-message';
import MessageItem from '../components/message';
import { clearTopUpInstructions } from './store/slices/message';

import ROUTES from '../routes';

import cn from './messages.module.css';
import useFilteredList from './helpers/use-filtered-list';

const getPspPartnerPaymentType = (type: string) => {
  let typeName;

  switch (type) {
    case PspPartnerPaymentType.BACS:
      typeName = 'BACS';
      break;
    case PspPartnerPaymentType.FASTER_PAYMENT:
      typeName = 'Faster payment';
      break;
    case PspPartnerPaymentType.SEPA_SCT:
      typeName = 'SEPA SCT';
      break;
    case PspPartnerPaymentType.SEPA_SCT_INST:
      typeName = 'SEPA SCT INST';
      break;
    case PspPartnerPaymentType.SWIFT:
      typeName = 'SWIFT';
      break;
    case PspPartnerPaymentType.TARGET_2:
      typeName = 'Target 2';
      break;
    case PspPartnerPaymentType.FEDWIRE:
      typeName = 'Fedwire';
      break;
    case PspPartnerPaymentType.CRYPTO_ASSET_PAYMENT:
      typeName = 'Crypto Asset Payment';
      break;
    case PspPartnerPaymentType.CASH_OVER_COUNTER:
      typeName = 'Cash Over Counter';
      break;
    default:
      typeName = type;
      break;
  }

  return typeName;
};

const Message = ({
  type,
  messageType,
}: {
  type: 'main' | 'requests';
  messageType: 'new' | 'in-progress' | 'completed' | 'all';
}) => {
  const [infoVisible, setInfoVisible] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const [popupVisible, setPopupVisible] = useState(false);
  const [assignPanelIsOpen, setAssignPanelIsOpen] = useState(false);
  const [attachmentsPanelIsOpen, setAttachmentsPanelIsOpen] = useState(false);
  const [topUpFilesSidepanelIsOpen, setTopUpFilesSidepanelIsOpen] =
    useState(false);
  const [addFilePopupVisible, setAddFilePopupVisible] = useState(false);
  const [attachmentsPanelState, setAttachmentsPanelState] = useState<
    'show' | 'closing'
  >('show');
  const [leavePopupVisible, setLeavePopupVisible] = useState(false);
  const [messagesIsFetching, setMessagesIsFetching] = useState<
    boolean | undefined
  >(undefined);
  const [currentScrollTop, setCurrentScrollTop] = useState(0);
  const [attachment, setAttachment] = useState<File | undefined>(undefined);
  const [uploadError, setUploadError] = useState('');
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([]);
  const [selectedTopUpId, setSelectedTopUpId] = useState('');
  const [topUpCurrency, setTopUpCurrency] = useState('');
  const [topUpAccount, setTopUpAccount] = useState('');
  const [selectedInstructionType, setInstructionType] = useState('');
  const [topUpName, setTopUpName] = useState('');
  const [isReopen, setIsReopen] = useState(false);
  const [searchPannelVisible, setSearchPannelVisible] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const [getChannelsDocuments] = useLazyGetChannelDocumentsQuery();

  const dispatch = useAppDispatch();

  const [getMessages, { isLoading, isFetching }] = useLazyGetMessagesQuery();
  const [
    getUserAccounts,
    { isLoading: acountsIsLoading, isFetching: accountsIsFetching },
  ] = useLazyGetUserAccountsQuery();
  const [getSelectedChannel] = useLazyGetSelectedChannelQuery();
  const [assignToChat, { isLoading: assignIsLoading, isSuccess }] =
    useAssignEmployeeToChatMutation();
  const [
    getMessagingTopUpOptions,
    { isLoading: topUpIsLoading, isFetching: topUpIsFetching },
  ] = useLazyGetMessagingTopUpQuery();
  const [
    getTopUpCurrencies,
    {
      isLoading: topUpCurrenciesIsLoading,
      isFetching: topUpCurrenciesIsFetching,
    },
  ] = useLazyGetTopUpCurrenciesQuery();
  const [
    getMessagingTopUpInstructions,
    { isLoading: instructionsIsLoading, isFetching: instructionsIsFetching },
  ] = useLazyGetMessagingTopUpInstructionsQuery();
  const [unreadMessages] = useUnreadMessagesMutation();

  const [
    changeChatStatus,
    {
      isLoading: changeChatStatusInProgress,
      isSuccess: changeChatStatusIsSucces,
      isError: changeChatStatusIsError,
    },
  ] = useChangeChatStatusMutation();

  const { sendMessage, scrollRef, connectionStatus, prevConnectionStatus } =
    useOutletContext<{
      sendMessage: SendMessage;
      scrollRef: RefObject<HTMLDivElement | null>;
      connectionStatus?: string;
      prevConnectionStatus?: string;
    }>();

  const markdownRef = useRef<MDXEditorMethods | null>(null);

  const prevMessageIsFetching = usePrevious(messagesIsFetching);

  const scrollWrapperRef = useRef<HTMLDivElement | null>(null);

  const { pathname } = useLocation();

  const {
    messages: {
      message: {
        messages,
        topUpOptions,
        messagesPagination,
        documents,
        accounts,
        baseCurrency,
        currencies,
        topUpInstructions,
        employees: storeEmployees,
        searchText,
      },
      general: { selectedChannel, employee, mainChannels, requestChannels },
    },
    messagesDictionaries: { transactionTypes },
  } = useAppSelector((state) => state);
  // const { employees } = useAppSelector(employeesSelector);

  const { list: employees, onSearch } = useFilteredList({
    list: storeEmployees || [],
    searchQuery: searchText,
  });

  const { activePermissions } = useAppSelector(
    (state) => state.messagesDictionaries,
  );

  const [
    getEmployees,
    { isLoading: employeeIsLoading, isFetching: employeeIsFetching },
  ] = useLazyGetMessagingEmployeesListQuery();
  const [upload, { isLoading: fileUploadInProgress }] =
    useMessagingUploadMutation();
  const [
    sendFile,
    { isLoading: sendFileInProgress, isSuccess: fileUploadIsSuccess },
  ] = useSendFileMutation();
  const [
    getMessagingTopUpInstructionPdf,
    { isLoading: pdfDownloadInProgress },
  ] = useLazyGetMessagingTopUpInstructionPdfQuery();

  const { messageId } = useParams();

  const prevMessageId = usePrevious(messageId);
  const prevMessages = usePrevious(messages);

  const navigate = useNavigate();

  const onAddFile = (files: FileList) => {
    setAttachment(files[0]);

    if (!addFilePopupVisible) {
      setAddFilePopupVisible(true);
    }
  };

  const onDeleteFile = () => {
    setAttachment(undefined);
  };

  const onUnread = async () => {
    if (messages && messages?.length > 0) {
      const lastMessage = messages[0];

      if (lastMessage) {
        const backPath = pathname.replace(`/${messageId}`, '');

        const response = (await unreadMessages({
          id: messageId as string,
          body: {
            messages: [lastMessage.id as string],
            channelId: messageId as string,
          },
        })) as { error?: ResponseError };

        if (!response.error) {
          dispatch(setSelectedChannel(undefined));

          if (messageType === 'in-progress') {
            if (type === 'main') {
              const nextMainChannels = mainChannels?.filter(
                (item) => item.id !== messageId,
              );

              dispatch(setMainChannels(nextMainChannels as Channel[]));
            } else if (type === 'requests') {
              const nextRequestsChannels = requestChannels?.filter(
                (item) => item.id !== messageId,
              );

              dispatch(setRequestChannels(nextRequestsChannels as Channel[]));
            }
          }

          navigate(backPath);
        }
      }
    }
  };

  const onSendMessage = async () => {
    const text = markdownRef?.current?.getMarkdown();

    const response = (await upload(attachment as File)) as unknown as {
      data: UploadResponse[];
      error?: any;
      message?: string;
    };

    if (response && response.data) {
      let fileName = attachment?.name;

      if (response.data[0] && response.data[0]?.mimeType?.includes('image')) {
        fileName = `${attachment?.name.slice(0, attachment?.name.lastIndexOf('.'))}.jpg`;
      }

      const documentResponse = (await sendFile({
        id: messageId as string,
        body: {
          data: [
            {
              uploadId: response?.data[0]?.id,
              name: fileName || topUpName,
            },
          ],
        },
      })) as { error?: any; data: { data: Document[] } };

      if (documentResponse && documentResponse.data) {
        setAttachment(undefined);
        setAddFilePopupVisible(false);
        setTopUpFilesSidepanelIsOpen(false);
        setTopUpName('');
        setSelectedTopUpId('');
        setTopUpCurrency('');
        setTopUpAccount('');
        dispatch(clearTopUpInstructions());

        sendMessageHelper({
          messageId: messageId as string,
          value: text?.replaceAll('\n\n\n\n', '\n\n&#x20;\n\n'),
          setMainChannels: (channels: Channel[]) =>
            dispatch(setMainChannels(channels)),
          setRequestChannels: (channels: Channel[]) =>
            dispatch(setRequestChannels(channels)),
          type,
          mainChannels,
          requestChannels,
        });

        sendMessage(
          JSON.stringify({
            event: 'message',
            data: {
              channelId: messageId,
              content: text?.replaceAll('\n\n\n\n', '\n\n&#x20;\n\n'),
              documentId: documentResponse.data?.data[0]?.id,
            },
          }),
        );

        const safariAgent = navigator.userAgent.indexOf('Safari') > -1;
        const chromeAgent = navigator.userAgent.indexOf('Chrome') > -1;

        let time = 5;

        if (!(safariAgent && chromeAgent)) {
          time = 40;
        }

        setTimeout(() => {
          if (scrollRef && scrollRef.current) {
            const { scrollHeight } = scrollRef.current;

            if (scrollHeight) {
              scrollRef.current.scrollTop = scrollHeight;
            }

            markdownRef?.current?.setMarkdown('');
          }
        }, time);
      } else if (documentResponse && documentResponse.error) {
        if (
          documentResponse?.error?.data?.message &&
          Array.isArray(documentResponse?.error?.data?.message) &&
          documentResponse?.error?.data?.message[0]
        ) {
          const errorText =
            documentResponse?.error?.data?.message[0].constraints &&
            Object.values(
              documentResponse?.error.data?.message[0].constraints,
            )[0];

          setUploadError(errorText);
        } else {
          setUploadError('Something was wrong. Try again');
        }
      }
    } else if (response && response.error) {
      if (
        response?.error?.originalStatus &&
        response?.error?.originalStatus === 413
      ) {
        setUploadError('File size too large');
      } else {
        setUploadError(
          response?.error?.data?.message || 'Something was wrong. Try again',
        );
      }
    }
  };

  const getNextMessages = async (cursor: string) => {
    setMessagesIsFetching(true);

    const response = (await getMessages({
      id: messageId as string,
      cursor,
    })) as { error?: any };

    if (response.error) {
      setMessagesIsFetching(false);
    } else {
      setMessagesIsFetching(false);
    }
  };

  const getTopUpInstructionPdfAction = async (
    index: number,
    instructionType: PspPartnerPaymentType,
    sendToChat?: boolean,
  ) => {
    setInstructionType(instructionType);

    const response = (await getMessagingTopUpInstructionPdf({
      id: topUpAccount,
      pspPartnerId: selectedTopUpId,
      currencyId: topUpCurrency,
      index,
    })) as any;

    if (response.data) {
      if (sendToChat) {
        setAddFilePopupVisible(true);

        const currentAccount = accounts?.find(
          (account) => account.id === topUpAccount,
        );
        const currentBank = topUpOptions?.find(
          (item) => item.id === selectedTopUpId,
        );
        const currentCurrency = currencies?.find(
          (item) => item.value === topUpCurrency,
        );

        const newFile = new File(
          [response.data],
          `Top-up options: ${currentBank?.companyName} (${currentCurrency?.name}), ${getPspPartnerPaymentType(
            instructionType,
          )}(${currentAccount?.fullNumber}).pdf`,
          {
            lastModified: new Date().getTime(),
            type: response.data.type,
          },
        );

        setAttachment(newFile);

        if (currentAccount && currentBank && currentCurrency) {
          setTopUpName(
            `Top-up options: ${currentBank.companyName} (${
              currentCurrency.name
            }), ${getPspPartnerPaymentType(
              instructionType,
            )}(${currentAccount?.fullNumber}).pdf`,
          );
        }
      } else {
        const currentAccount = accounts?.find(
          (account) => account.id === topUpAccount,
        );

        const hiddenElement = document.createElement('a');
        const url = window.URL || window.webkitURL;

        // @ts-ignore
        const blob = url.createObjectURL(response.data);
        hiddenElement.href = blob;
        hiddenElement.target = '_blank';
        hiddenElement.download = `${getPspPartnerPaymentType(
          instructionType,
        )}(${currentAccount?.fullNumber}).pdf`;
        hiddenElement.click();
      }

      setInstructionType('');
    }
  };

  let path: string;
  let backPath: string;

  if (type === 'main') {
    path = generatePath(ROUTES.MESSAGES_MAIN_IN_PROGRESS_MESSAGE, {
      messageId: messageId as string,
    });

    if (messageType === 'new') {
      backPath = ROUTES.MESSAGES_MAIN_UNREAD;
    } else if (messageType === 'in-progress') {
      backPath = ROUTES.MESSAGES_MAIN_IN_PROGRESS;
    } else if (messageType === 'completed') {
      backPath = ROUTES.MESSAGES_MAIN_COMPLETED;
    } else {
      backPath = ROUTES.MESSAGES_MAIN_ALL;
    }
  } else {
    path = generatePath(ROUTES.MESSAGES_REQUEST_IN_PROGRESS_MESSAGE, {
      messageId: messageId as string,
    });

    if (messageType === 'new') {
      backPath = ROUTES.MESSAGES_REQUEST_UNREAD;
    } else if (messageType === 'in-progress') {
      backPath = ROUTES.MESSAGES_REQUEST_IN_PROGRESS;
    } else if (messageType === 'completed') {
      backPath = ROUTES.MESSAGES_REQUEST_COMPLETED;
    } else {
      backPath = ROUTES.MESSAGES_REQUEST_ALL;
    }
  }

  useEffect(() => {
    if (isSuccess) {
      setLeavePopupVisible(false);
      navigate(backPath);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (messageId && prevMessageId !== messageId) {
      getMessages({ id: messageId });
      getChannelsDocuments(messageId);

      if (!selectedChannel) {
        getSelectedChannel(messageId);

        sendMessage(
          JSON.stringify({
            event: 'subscribe',
            data: { channelId: messageId },
          }),
        );
      }
    }
  }, [messageId, prevMessageId]);

  useEffect(() => {
    if (
      connectionStatus !== prevConnectionStatus &&
      prevConnectionStatus === 'Closed' &&
      messageId
    ) {
      getMessages({ id: messageId });
      getChannelsDocuments(messageId);

      sendMessage(
        JSON.stringify({
          event: 'subscribe',
          data: { channelId: messageId },
        }),
      );
    }
  }, [connectionStatus, prevConnectionStatus, messageId]);

  useEffect(() => {
    if (selectedChannel && selectedChannel?.organizationId) {
      getUserAccounts(selectedChannel.organizationId);
    }
  }, [selectedChannel]);

  useEffect(() => {
    if (prevMessageId && prevMessageId !== messageId) {
      setMessagesIsFetching(undefined);

      dispatch(setScrolled(false));

      sendMessage(
        JSON.stringify({
          event: 'unsubscribe',
          data: { channelId: prevMessageId },
        }),
      );
    }
  }, [prevMessageId, messageId]);

  useEffect(() => {
    if (
      messages &&
      prevMessages &&
      !isEqual(messages, prevMessages) &&
      scrollRef &&
      scrollRef?.current &&
      prevMessageIsFetching
    ) {
      setTimeout(() => {
        if (scrollRef && scrollRef.current) {
          const { scrollHeight } = scrollRef.current;

          if (scrollHeight) {
            scrollRef.current.scrollTop = scrollHeight - currentScrollTop + 75;
            setCurrentScrollTop(scrollHeight);
          }
        }
      }, 1);
    }
  }, [
    messages,
    prevMessages,
    scrollRef,
    currentScrollTop,
    prevMessageIsFetching,
    messagesIsFetching,
  ]);

  useEffect(() => {
    if (topUpFilesSidepanelIsOpen) {
      getMessagingTopUpOptions();
      getTopUpCurrencies();
    }
  }, [topUpFilesSidepanelIsOpen]);

  useEffect(() => {
    if (topUpAccount && selectedTopUpId && topUpCurrency) {
      getMessagingTopUpInstructions({
        id: topUpAccount,
        pspPartnerId: selectedTopUpId,
        currencyId: topUpCurrency,
      });
    }
  }, [topUpAccount, selectedTopUpId, topUpCurrency]);

  useEffect(() => {
    if (changeChatStatusIsSucces) {
      setPopupVisible(false);

      if (!isReopen) {
        navigate(backPath);
      }

      if (attachmentsPanelIsOpen) {
        setAttachmentsPanelIsOpen(false);
      }
    }
  }, [changeChatStatusIsSucces, isReopen]);

  useEffect(() => {
    if (changeChatStatusIsError) {
      setPopupVisible(false);
    }
  }, [changeChatStatusIsError]);

  let content;
  let assignText;

  if (
    isLoading ||
    accountsIsFetching ||
    acountsIsLoading ||
    (isFetching && !messagesIsFetching)
  ) {
    content = (
      <div className={cn.messageLoader}>
        <IconLoader />
      </div>
    );
  } else {
    if (selectedChannel?.employee) {
      if (selectedChannel.employeeId === employee?.id) {
        assignText = 'Assigned to You';
      } else {
        assignText = (
          <NavLink
            to={`/administration/users/${selectedChannel?.employeeId}/edit`}
            className={`${cn.assignedText} ${
              !activePermissions?.includes(
                PermissionEnum.SECTION_ADMINISTRATION,
              ) || !activePermissions?.includes(PermissionEnum.USERS_LIST)
                ? cn.assignedTextDisabled
                : ''
            }`}
            target="_blank"
          >
            <span>Assigned to </span>
            {`${selectedChannel.employee?.profile?.firstName} ${selectedChannel.employee?.profile?.lastName} (${selectedChannel?.employee?.email})`}
          </NavLink>
        );
      }
    }

    let userLink;
    let userType;
    let transactionLink;
    let infoTitle;

    if (selectedChannel?.transaction?.typeId) {
      const currentType = transactionTypes?.find(
        (item) => item.id === selectedChannel?.transaction?.typeId,
      )?.name;

      if (currentType) {
        if (currentType === TransactionTypeEnum.INCOMING) {
          transactionLink = `/transactions/incoming/${selectedChannel?.transactionId}`;
        } else if (currentType === TransactionTypeEnum.OUTGOING) {
          transactionLink = `/transactions/outgoing/${selectedChannel?.transactionId}`;
        } else if (currentType === TransactionTypeEnum.INTERNAL) {
          transactionLink = `/transactions/internal/${selectedChannel?.transactionId}`;
        } else if (currentType === TransactionTypeEnum.CURRENCY_EXCHANGE) {
          transactionLink = `/transactions/exchange/${selectedChannel?.transactionId}`;
        } else if (currentType === TransactionTypeEnum.NOSTRO) {
          transactionLink = `/transactions/general-ledger/${selectedChannel?.transactionId}`;
        } else if (currentType === TransactionTypeEnum.GENERAL_LEDGER) {
          transactionLink = `/transactions/nostro/${selectedChannel?.transactionId}`;
        }
      }
    }

    if (selectedChannel?.application) {
      if (selectedChannel?.application?.type === CustomerType.CORPORATE) {
        userLink = `/customers/new-application/corporate/${selectedChannel?.applicationId}`;
        userType = 'Corporate application';
      } else if (
        selectedChannel?.application?.type === CustomerType.INDIVIDUAL
      ) {
        userLink = `/customers/new-application/individual/${selectedChannel?.applicationId}`;
        userType = 'Individual application';
      } else if (
        selectedChannel?.application?.type === CustomerType.COMPANY_OFFICIAL
      ) {
        userLink = `/customers/new-application/company-official/${selectedChannel?.applicationId}`;
        userType = 'Company Official application';
      }

      infoTitle = (
        <NavLink to={userLink as string} target="_blank">
          {formatTitle(selectedChannel)}
        </NavLink>
      );
    } else if (selectedChannel?.organization) {
      if (selectedChannel?.organization?.type === CustomerType.CORPORATE) {
        userLink = `/customers/corporate/${selectedChannel?.organizationId}`;
        userType = 'Corporate customer';
      } else if (
        selectedChannel?.organization?.type === CustomerType.INDIVIDUAL ||
        selectedChannel?.organization?.type === CustomerType.COMPANY_OFFICIAL
      ) {
        userLink = `/customers/individual/${selectedChannel?.organizationId}`;
        userType = 'Individual customer';
      }

      infoTitle = (
        <div>
          {selectedChannel?.transactionId ? (
            <NavLink to={transactionLink as string} target="_blank">
              {formatTitle(selectedChannel)}
            </NavLink>
          ) : (
            formatTitle(selectedChannel, true)
          )}
        </div>
      );
    } else if (selectedChannel?.channelType === ChannelType.DEMO) {
      userType = 'Demo customer';
    }

    content = (
      <>
        <div className={cn.messageHeader}>
          <ClickOutsideDetector
            setOptionsListVisible={setInfoVisible}
            className={cn.messageHeaderHintWrapper}
          >
            <>
              <button
                type="button"
                className={cn.messageHeaderHint}
                onClick={() => setInfoVisible(!infoVisible)}
              >
                i
              </button>
              {infoVisible && (
                <div className={cn.messageHeaderCustomerInfo}>
                  <div>{infoTitle}</div>
                  {selectedChannel?.organization &&
                    !selectedChannel?.organization.companyName && (
                      <div>{`${
                        new Date().getFullYear() -
                        new Date(
                          selectedChannel?.user?.profile?.dob as string,
                        ).getFullYear()
                      } y.o`}</div>
                    )}
                  <div>{userType}</div>
                  {selectedChannel?.organization && (
                    <div>
                      <NavLink to={userLink as string} target="_blank">
                        {selectedChannel?.organization?.firstName
                          ? `${selectedChannel?.organization?.firstName} ${selectedChannel?.organization?.lastName}`
                          : `${selectedChannel?.organization?.companyName} (${selectedChannel?.user?.profile?.firstName} ${selectedChannel?.user?.profile?.lastName})`}
                      </NavLink>
                    </div>
                  )}
                  {!selectedChannel?.organization &&
                    selectedChannel?.application && (
                      <div>
                        <NavLink to={userLink as string} target="_blank">
                          {selectedChannel?.application?.firstName
                            ? `${selectedChannel?.application?.firstName} ${selectedChannel?.application?.lastName}`
                            : selectedChannel?.application?.companyName}
                        </NavLink>
                      </div>
                    )}
                  <div>{selectedChannel?.user?.phone}</div>
                  <div>{selectedChannel?.user?.email}</div>
                </div>
              )}
            </>
          </ClickOutsideDetector>
          <div
            className={`${cn.messageHeaderLeft} ${
              selectedChannel?.employeeId === employee?.id ? cn.moreWidth : ''
            } ${!selectedChannel?.employeeId ? cn.fullWidth : ''}`}
          >
            <div className={cn.messageHeaderLinks}>
              <NavLink
                className={`${cn.messageHeaderTitle} ${
                  !userLink ? cn.messageHeaderTitleDisabled : ''
                } ${selectedChannel?.transactionId ? cn.withMarginTop : ''}`}
                to={userLink || ''}
                target="_blank"
              >
                <span>{selectedChannel && formatTitle(selectedChannel)}</span>
                {userLink && <IconRight height={12} width={9} />}
              </NavLink>
              {selectedChannel?.transactionId && (
                <NavLink
                  className={`${cn.messageHeaderTransaction} ${
                    !selectedChannel?.transaction?.typeId
                      ? cn.messageHeaderTransactionDisabled
                      : ''
                  }`}
                  to={transactionLink || ''}
                  target="_blank"
                >
                  <span>
                    {`Transaction ${selectedChannel?.transactionId} (${selectedChannel?.user?.profile?.firstName} ${selectedChannel?.user?.profile?.lastName})`}
                  </span>
                </NavLink>
              )}
            </div>
          </div>
          <div
            className={`${cn.messageHeaderRight} ${
              selectedChannel?.employeeId === employee?.id ? cn.minWidth : ''
            } ${!selectedChannel?.employeeId ? cn.withoutWidth : ''}`}
          >
            {selectedChannel?.employee && (
              <div className={cn.messageAssigned}>{assignText}</div>
            )}
            <button
              type="button"
              className={`${cn.searchButton} ${
                searchPannelVisible ? cn.active : ''
              }`}
              aria-label="search button"
              onClick={() => setSearchPannelVisible(!searchPannelVisible)}
            >
              <IconSearch />
            </button>
            <ClickOutsideDetector
              setOptionsListVisible={setMenuVisible}
              className={cn.messageHeaderMenuWrapper}
            >
              <>
                <button
                  type="button"
                  className={cn.messageHeaderMenuButton}
                  onClick={() => setMenuVisible(!menuVisible)}
                  aria-label="Menu button"
                >
                  <span />
                </button>
                {menuVisible && (
                  <div className={cn.messagesHeaderMenu}>
                    <button
                      type="button"
                      className={cn.messageHeaderMenuItem}
                      onClick={() => setAttachmentsPanelIsOpen(true)}
                    >
                      Show attachment
                      <IconAttach
                        width={20}
                        height={20}
                        className={cn.attachIcon}
                      />
                    </button>
                    <div className={cn.separator} />
                    {activePermissions?.includes(
                      PermissionEnum.MESSAGES_ASSIGN_CHAT_TO_ANOTHER_ACTION,
                    ) && (
                      <button
                        type="button"
                        className={cn.messageHeaderMenuItem}
                        onClick={() => {
                          setAssignPanelIsOpen(true);
                          getEmployees();
                        }}
                        disabled={
                          selectedChannel?.status === ChannelStatus.CLOSED
                        }
                      >
                        Assign
                        <IconUser
                          className={cn.arrowIcon}
                          width={18}
                          height={18}
                        />
                      </button>
                    )}
                    {messages &&
                      messages?.length > 0 &&
                      !messages?.find((item) => !item.readAt) && (
                        <button
                          type="button"
                          className={cn.messageHeaderMenuItem}
                          onClick={() => {
                            onUnread();
                            setMenuVisible(false);
                          }}
                        >
                          Mark as unread
                          <div className={cn.circleUnread} />
                        </button>
                      )}

                    {selectedChannel?.employeeId &&
                      selectedChannel?.employeeId === employee?.id && (
                        <button
                          type="button"
                          className={cn.messageHeaderMenuItem}
                          onClick={() => {
                            setLeavePopupVisible(true);
                            setMenuVisible(false);
                          }}
                          disabled={
                            selectedChannel?.status === ChannelStatus.CLOSED
                          }
                        >
                          Leave
                          <IconUpload
                            className={cn.leaveIcon}
                            width={16}
                            height={16}
                          />
                        </button>
                      )}
                    {type === 'requests' &&
                      selectedChannel?.channelType ===
                        ChannelType.TRANSACTION && (
                        <button
                          type="button"
                          className={cn.messageHeaderMenuItem}
                          onClick={() => {
                            dispatch(setSelectedChannelTab(messageType));

                            if (documents && documents?.length > 0) {
                              setAttachmentsPanelState('closing');
                              setAttachmentsPanelIsOpen(true);
                            } else {
                              setPopupVisible(true);
                            }
                          }}
                          disabled={
                            selectedChannel?.status === ChannelStatus.CLOSED
                          }
                        >
                          Close chat
                          <IconSelected
                            className={cn.blockIcon}
                            width={14}
                            height={14}
                          />
                        </button>
                      )}
                  </div>
                )}
              </>
            </ClickOutsideDetector>
          </div>
          {searchPannelVisible && (
            <div className={cn.searchPannel}>
              <InputText
                name="searchInput"
                prefix={<IconSearch />}
                placeholder="Search..."
                size={InputSize.SMALL}
                className={cn.searchInput}
                prefixClassName={cn.searchIcon}
                onChange={(e) => setSearchValue(e.target.value)}
                value={searchValue}
              />
              <button
                type="button"
                className={cn.closePanelButton}
                onClick={() => {
                  setSearchPannelVisible(false);
                  setSearchValue('');
                }}
                aria-label="Close panel"
              >
                <IconClose width={16} height={16} />
              </button>
            </div>
          )}
        </div>
        <MessageItem
          messages={messages}
          path={path}
          setAssignPanelIsOpen={setAssignPanelIsOpen}
          getEmployees={() => getEmployees()}
          messageId={messageId}
          setCurrentScrollTop={setCurrentScrollTop}
          scrollWrapperRef={scrollWrapperRef}
          getNextMessages={getNextMessages}
          pagination={messagesPagination}
          messagesIsFetching={messagesIsFetching}
          scrollRef={scrollRef}
          prevMessageIsFetching={prevMessageIsFetching}
          setTopUpFilesSidepanelIsOpen={setTopUpFilesSidepanelIsOpen}
          onAddFile={onAddFile}
          accounts={accounts}
          sendMessage={sendMessage}
          changeChatStatusInProgress={changeChatStatusInProgress}
          type={type}
          onReopen={() => {
            dispatch(setSelectedChannelTab(messageType));

            setIsReopen(true);

            changeChatStatus({
              id: messageId as string,
              body: {
                status: ChannelStatus.OPEN,
              },
            });
          }}
        />
      </>
    );
  }

  return (
    <div
      className={`${cn.messagesContentRight} ${
        isLoading ? cn.messagesContentRightEmpty : ''
      }`}
    >
      {content}
      <Popup
        title={
          selectedDocumentIds?.length
            ? `Close the chat and save ${selectedDocumentIds?.length} attachments in the client's profile.`
            : 'Close chat'
        }
        description="The client case will be closed. You can reopen it later."
        onCancelClick={() => setPopupVisible(false)}
        onOkClick={() => {
          setIsReopen(false);

          changeChatStatus({
            id: messageId as string,
            body: {
              status: ChannelStatus.CLOSED,
            },
          });
        }}
        cancelButtonText="Cancel"
        okButtonText={changeChatStatusInProgress ? 'Loading...' : 'Close chat'}
        popupClassName={cn.closeChatPopup}
        isVisible={popupVisible}
        parent={document.getElementById('employee-popup') as HTMLDivElement}
      />
      <AssignSidePanel
        panelIsOpen={assignPanelIsOpen}
        setAssignPanelIsOpen={setAssignPanelIsOpen}
        employees={employees || []}
        isLoading={employeeIsLoading || employeeIsFetching}
        messageId={messageId}
        onSearch={onSearch}
        path={path}
      />
      <AttachmentSidepanel
        panelIsOpen={attachmentsPanelIsOpen}
        setPanelIsOpen={setAttachmentsPanelIsOpen}
        messageId={messageId}
        panelState={attachmentsPanelState}
        setCloseChatPopupVisible={setPopupVisible}
        selectedDocumentIds={selectedDocumentIds}
        setSelectedDocumentIds={setSelectedDocumentIds}
        organizationType={selectedChannel?.organization?.type}
        channelType={selectedChannel?.channelType}
        organizationId={selectedChannel?.organizationId}
      />
      <AddFiles
        panelIsOpen={topUpFilesSidepanelIsOpen}
        setPanelIsOpen={setTopUpFilesSidepanelIsOpen}
        popupVisible={addFilePopupVisible}
        setPopupVisible={setAddFilePopupVisible}
        attachment={attachment}
        onDeleteFile={onDeleteFile}
        requestInProgress={fileUploadInProgress || sendFileInProgress}
        onSendMessage={onSendMessage}
        fileUploadIsSuccess={fileUploadIsSuccess}
        uploadError={uploadError}
        selectedTopUpId={selectedTopUpId}
        topUpOptions={topUpOptions}
        setSelectedTopUpId={setSelectedTopUpId}
        setUploadError={setUploadError}
        topUpIsLoading={
          topUpIsLoading ||
          topUpIsFetching ||
          topUpCurrenciesIsLoading ||
          topUpCurrenciesIsFetching
        }
        currencies={currencies}
        baseCurrency={baseCurrency}
        topUpCurrency={topUpCurrency}
        setTopUpCurrency={setTopUpCurrency}
        topUpAccount={topUpAccount}
        setTopUpAccount={setTopUpAccount}
        accounts={accounts}
        instructionsIsLoading={instructionsIsLoading || instructionsIsFetching}
        topUpInstructions={topUpInstructions}
        selectedInstructionType={selectedInstructionType}
        getTopUpInstructionPdf={getTopUpInstructionPdfAction}
        pdfDownloadInProgress={pdfDownloadInProgress}
        topUpName={topUpName}
        markdownRef={markdownRef}
      />
      <Popup
        title="Leave the chat?"
        onCancelClick={() => {
          setLeavePopupVisible(false);
        }}
        onOkClick={() =>
          assignToChat({
            id: messageId as string,
            body: {
              employeeId: null,
            },
          })
        }
        cancelButtonText="Cancel"
        okButtonText={assignIsLoading ? 'Loading...' : 'Leave'}
        isVisible={leavePopupVisible}
      />
      <div id="add-file-popup" />
    </div>
  );
};

export default Message;
